<script setup>
const elementsRefs = ref([]);
const elementsVisibility = ref([]);
let observer = false;

const route = useRoute();

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const index = elementsRefs.value.indexOf(entry.target);
        if (index !== -1 && entry.isIntersecting) {
          elementsVisibility.value[index] = true;
        }
      });
    },
    { threshold: 0.1 }
  );

  elementsRefs.value.forEach((el) => {
    if (el) observer.observe(el);
  });
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

const { locale, t } = useI18n();
const localePath = useLocalePath();

import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
});

const pageQuery = gql`
  query pageData {
    page(id: 4) {
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      id
      name
      content
      children {
        id
        name
        image
        route_id
        external_link
        slug: seo_url_slug {
          cs
          en
        }
        children {
          id
          name
          route_id
          external_link
          slug: seo_url_slug {
            cs
            en
          }
        }
      }
    }
    buildings {
      id
      name
      slug: seo_url_slug {
        cs
        en
      }
    }
  }
`;

const { data } = await useAsyncQuery(pageQuery);

const page = computed(() => data.value?.page);
const buildings = computed(() => data.value?.buildings);

const breadcrumbs = computed(() => {
  return page.value && page.value?.name ? [{ title: page.value?.name }] : [];
});

const pageTileLink = (route_id, slug) => {
  return route_id
    ? localePath(useNoTemplating(route_id))
    : localePath({
        name: 'about-page-slug',
        params: { slug: slug[locale.value] },
      });
};

useHeadData({
  title: page.value?.seo_meta_title || page.value?.name || null,
  description: page.value?.seo_meta_description || null,
  keywords: page.value?.seo_meta_keywords || null,
  image: page.value?.image || page.value?.cropped_detail_image || null,
  url: route.fullPath,
});
</script>

<template>
  <main class="main" v-if="page">
    <Breadcrumbs :items="breadcrumbs" />
    <h1 :ref="(el) => (elementsRefs[0] = el)" class="heading" :class="{ 'visible animate__animated animate__fadeInUp': elementsVisibility[0], invisible: !elementsVisibility[0] }">
      {{ t('pages.about.information-title') }}
    </h1>

    <div class="b-flex mt-5">
      <div
        v-for="(child, childIndex) in page.children"
        :key="child.id"
        :ref="(el) => (elementsRefs[childIndex + 1] = el)"
        class="mt-4 b-flex-child"
        :class="{
          full: useNoTemplating(child.route_id) === 'about-collections' || useNoTemplating(child.route_id) === 'about-buildings',
          'visible animate__animated animate__fadeInUp': elementsVisibility[childIndex + 1],
          invisible: !elementsVisibility[childIndex + 1],
        }"
      >
        <h2 v-if="useNoTemplating(child.route_id) == 'about-collections'" class="heading mt-5 mb-4">
          {{ t('pages.about.about-title') }}
        </h2>

        <PageTile :image="useGetImageUrl(child.image)" :title="child.name" :link="pageTileLink(child.route_id, child.slug)" />

        <p v-if="useNoTemplating(child.route_id) === 'about-buildings'" class="text text--2 mt-4">
          <span v-for="(building, buildingIndex) in buildings" :key="building.id">
            <nuxt-link
              class="link"
              :to="
                localePath({
                  name: 'about-buildings-slug',
                  params: { slug: building.slug[locale] },
                })
              "
            >
              {{ building.name }}
            </nuxt-link>
            <span v-if="buildingIndex < buildings.length - 1">{{ ', ' }} </span>
          </span>
        </p>

        <p v-else-if="useNoTemplating(child.route_id) === 'about-collections'" class="text text--2 mt-4">
          <span v-for="(grandchild, grandchildIndex) in child.children" :key="grandchild.id">
            <nuxt-link
              class="link"
              :to="
                localePath({
                  name: 'about-collections-slug',
                  params: { slug: grandchild.slug[locale] },
                })
              "
            >
              {{ grandchild.name }}
            </nuxt-link>
            <span v-if="grandchildIndex < child.children.length - 1">{{ ', ' }} </span>
          </span>
        </p>

        <p v-else class="text text--2 mt-4">
          <span v-for="(grandchild, grandchildIndex) in child.children" :key="grandchild.id">
            <a v-if="grandchild.external_link" :href="grandchild.external_link" target="_blank" class="link">
              {{ grandchild.name }}
            </a>
            <nuxt-link
              v-else
              class="link"
              :to="
                localePath({
                  name: 'about-page-slug',
                  params: { slug: grandchild.slug[locale] },
                })
              "
            >
              {{ grandchild.name }}
            </nuxt-link>
            <span v-if="grandchildIndex < child.children.length - 1">{{ ', ' }} </span>
          </span>
        </p>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.full {
  min-width: calc(100% - 24px);
  margin-bottom: 3rem;
}
.b-flex {
  margin-right: -1.5rem;
}
</style>
